import { useQuery } from '@tanstack/react-query';
import { Headphones } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { fetchCadeAudioRecordings } from '@/services/cade';
import type { CadeAudioRecording } from '@/services/types';

/**
 * Recordings by the day in CADE's own file name, which agreed with the session's parsed date
 * on every session that carries both. Built from the recordings list the audio screen already
 * fetches, so a session link costs no extra request and the lookup is shared by every row.
 */
function useRecordingLookup() {
  const { data } = useQuery({
    queryKey: ['cade', 'audio', 'recordings'],
    queryFn: () => fetchCadeAudioRecordings(),
    staleTime: 5 * 60 * 1000,
  });
  return useMemo(() => {
    const byDate = new Map<string, CadeAudioRecording[]>();
    for (const row of data?.recordings.value ?? []) {
      const date = row.recording.session_date;
      if (date) byDate.set(date, [...(byDate.get(date) ?? []), row.recording]);
    }
    /**
     * The recording of the session held on `date`, or null. Where two recordings share a day
     * the kind (ordinary or extraordinary) decides; if it cannot, no link is shown rather
     * than one that may open the wrong session.
     */
    return (date: string | null | undefined, sessionType?: string | null): string | null => {
      const found = date ? (byDate.get(date) ?? []) : [];
      if (found.length === 0) return null;
      // The files of one session (its parts) are one session: link its first part.
      const first = (list: CadeAudioRecording[]) =>
        [...list].sort((a, b) => (a.part ?? 0) - (b.part ?? 0))[0].key;
      const oneSession = (list: CadeAudioRecording[]) =>
        list.every((r) => r.kind === list[0].kind && r.number === list[0].number);
      if (oneSession(found)) return first(found);
      if (!sessionType) return null;
      const kind = /^extra/i.test(sessionType) ? 'EXTRAORDINARY' : 'ORDINARY';
      const same = found.filter((r) => r.kind === kind);
      return same.length > 0 && oneSession(same) ? first(same) : null;
    };
  }, [data]);
}

/** "Listen to the session": a link to the recording of the session held on `date`, if any. */
export function RecordingLink({
  date,
  sessionType,
}: {
  date: string | null | undefined;
  sessionType?: string | null;
}) {
  const { t } = useTranslation(['cade']);
  const key = useRecordingLookup()(date, sessionType);
  if (!key) return null;
  return (
    <Link
      to={`/cade/audio/${key}`}
      className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
      data-recording-link
    >
      <Headphones className="h-3 w-3" aria-hidden />
      {t('cade:audio.listenToSession')}
    </Link>
  );
}
